import { useMemo } from 'react'
import { useFieldArray, useFormContext } from 'react-hook-form'
import { Plus, Trash2 } from 'lucide-react'

type Props = {
  onAddVariant: (productId: number, productName: string) => void
  onSelectProduct: () => void
}

type LineItem = {
  product_id: number
  product_name: string
  variant_id: number
  quantity: number
  variant_name: string
  variant_sku: string
  variant_format: string
}

type ProductGroup = {
  productId: number
  productName: string
  items: Array<LineItem & { index: number }>
}

export function MobileProductSelectionStep({
  onAddVariant,
  onSelectProduct,
}: Props) {
  const {
    register,
    control,
    formState: { errors },
    watch,
  } = useFormContext()

  const { fields, remove } = useFieldArray({
    control,
    name: 'line_items',
  })

  const lineItems: LineItem[] = watch('line_items') ?? []
  const fieldArrayError = (errors as any)?.line_items

  // Group line items by product
  const productGroups = useMemo<ProductGroup[]>(() => {
    const groups: Record<number, ProductGroup> = {}

    lineItems.forEach((item, index) => {
      const productId = item.product_id
      if (!groups[productId]) {
        groups[productId] = {
          productId,
          productName: item.product_name,
          items: [],
        }
      }
      groups[productId].items.push({ ...item, index })
    })

    return Object.values(groups)
  }, [lineItems])

  return (
    <div className="p-2 space-y-4">
      {/* Samples Included */}
      <div className="space-y-2">
        <div className="text-xs font-semibold tracking-[0.12em] text-gray-500 uppercase">
          Samples Included
        </div>

        {fields.length === 0 && (
          <div className="rounded-lg border border-gray-200 bg-white p-3">
            <p className="text-xs text-gray-500">
              No products selected yet. Use Select Product to add one.
            </p>
          </div>
        )}

        {productGroups.map((group) => (
          <div
            key={group.productId}
            className="rounded-lg bg-gray-50 border-l-2 border-gray-200 p-3 text-xs text-gray-800 space-y-3"
          >
            {/* Product name header */}
            <div className="text-sm font-semibold text-gray-900">
              {group.productName}
            </div>

            {group.items.map((item) => (
              <div
                key={item.variant_id}
                className="rounded-md  bg-gray-50 p-2 space-y-2"
              >
                {/* SKU / name / delete */}
                <div className="flex items-start justify-between gap-2">
                  <div className="flex-1">
                    {item.variant_sku && (
                      <div className="text-xs text-gray-500">
                        SKU: {item.variant_sku}
                      </div>
                    )}
                    <div className="text-xs font-semibold text-gray-900">
                      {item.variant_name || 'Variant'}
                    </div>
                  </div>
                  <button
                    type="button"
                    className="flex items-center gap-1 text-xs text-red-600"
                    onClick={() => remove(item.index)}
                  >
                    <Trash2 className="h-3 w-3" />
                  </button>
                </div>

                {/* Quantity + Format */}
                <div className="flex items-center gap-3 text-xs text-gray-700">
                  <div className="flex items-center gap-1">
                    <span>Quantity:</span>
                    <input
                      type="number"
                      min={1}
                      className="w-16 rounded-md border border-gray-300 px-2 py-1 text-xs focus:border-emerald-600 focus:outline-none"
                      {...register(`line_items.${item.index}.quantity`, {
                        valueAsNumber: true,
                      })}
                    />
                  </div>

                  <div className="flex items-center gap-1 ml-auto">
                    <span>Format:</span>
                    <span className="text-gray-800">
                      {item.variant_format || 'N/A'}
                    </span>
                  </div>
                </div>

                {/* hidden variant_id input */}
                <input
                  type="hidden"
                  {...register(`line_items.${item.index}.variant_id`, {
                    valueAsNumber: true,
                  })}
                />

                {(fieldArrayError?.[item.index]?.quantity as any)?.message && (
                  <p className="mt-1 text-xs text-red-600">
                    {String(fieldArrayError[item.index].quantity.message)}
                  </p>
                )}
              </div>
            ))}

            {/* Add Variant link for this product */}
            <div className="flex justify-end">
              <button
                type="button"
                onClick={() => onAddVariant(group.productId, group.productName)}
                className="inline-flex items-center text-xs font-medium text-emerald-700"
              >
                <Plus className="mr-1 h-3 w-3" />
                Add Variant
              </button>
            </div>
          </div>
        ))}

        {typeof fieldArrayError?.message === 'string' && (
          <p className="mt-1 text-xs text-red-600">{fieldArrayError.message}</p>
        )}
      </div>

      {/* Add Products */}
      <div className="space-y-1">
        <div className="text-xs font-semibold tracking-[0.12em] text-gray-500 uppercase flex items-baseline">
          Add Products
        </div>
        <button
          type="button"
          onClick={onSelectProduct}
          className="flex w-full items-center justify-start gap-2 rounded-lg border border-emerald-700 bg-emerald-50 px-3 py-2 text-sm font-medium text-emerald-800"
        >
          <Plus className="h-4 w-4" />
          Select Product
        </button>
      </div>
    </div>
  )
}
